import type { Block } from '@kioskforge/shared';

export type ClockWidget = Extract<Block, { type: 'clock' }>;

/**
 * X3.2 (frontend half) — clock presentation logic, kept pure and deterministic.
 *
 * A preview must render the *same* pixels on every run, so the sample instant is
 * frozen rather than read from the system clock: `new Date()` would defeat the
 * golden-image test A adds for the SVG renderer and make the preview flicker.
 * The player (f10) passes a live date to the same helpers.
 */
export const CLOCK_SAMPLE = { hour: 9, minute: 41, second: 7 } as const;

/** Midnight-anchored sample instant (UTC; the display layer owns the zone). */
export const CLOCK_SAMPLE_ISO = '2024-01-01T09:41:07Z';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** 0–11 hour number used by the analog hand (12 o'clock = 0°). */
export function hourHandDegrees(hour24: number, minute: number): number {
  return ((hour24 % 12) + minute / 60) * 30;
}

export function minuteHandDegrees(minute: number): number {
  return minute * 6;
}

/** `'09:41'` / `'9:41'` — 24 h by default, 12 h drops the leading zero. */
export function formatClockTime(hour24: number, minute: number, format: '12h' | '24h'): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  if (format === '24h') return `${pad(hour24)}:${pad(minute)}`;
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${pad(minute)}`;
}

export function meridiem(hour24: number): 'AM' | 'PM' {
  return hour24 < 12 ? 'AM' : 'PM';
}

/** `'Mon 1 Jan'` (the default `date_format` is a compact day-month). */
export function formatClockDate(date: Date): string {
  return `${DAY_NAMES[date.getUTCDay()]} ${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()]}`;
}

const ONES = ['twelve', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven'] as const;
const MINUTE_WORDS: Record<number, string> = {
  0: "o'clock", 5: 'five past', 10: 'ten past', 15: 'quarter past', 20: 'twenty past', 25: 'twenty-five past',
  30: 'half past', 35: 'twenty-five to', 40: 'twenty to', 45: 'quarter to', 50: 'ten to', 55: 'five to'
};

/**
 * The 5-minute word clock: `"twenty to ten"`. Minutes round to the nearest five
 * (as a real word clock does); a "to" phrase (35–55) already names the next hour.
 */
export function toWordClock(hour24: number, minute: number): string {
  const rounded = Math.round(minute / 5) * 5;
  const bucket = rounded >= 60 ? 0 : rounded;
  // A "to" phrase (35–55) has already rolled forward to the next hour:
  // 9:40 is "twenty to ten", 10:58 is "eleven o'clock".
  const roll = rounded >= 35;
  const hour = (((hour24 + (roll ? 1 : 0)) % 24) + 24) % 24;
  const hourWord = ONES[hour % 12];
  if (bucket === 0) return `${hourWord} ${MINUTE_WORDS[0]}`;
  return `${MINUTE_WORDS[bucket]} ${hourWord}`;
}

/** Days/hours remaining until `to` as measured from the frozen sample. */
export function countdownRemaining(to: string): string {
  const target = Date.parse(to);
  if (Number.isNaN(target)) return '—';
  const ms = Math.max(0, target - Date.parse(CLOCK_SAMPLE_ISO));
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  return days > 0 ? `${days}d ${String(hours).padStart(2, '0')}h` : `${hours}h`;
}

export type ClockFace = {
  time: string;
  seconds: string;
  meridiem: '' | 'AM' | 'PM';
  date: string;
  words: string;
  hands: { hour: number; minute: number };
};

/** Everything a clock style needs to draw, derived in one deterministic pass. */
export function clockFace(block: ClockWidget): ClockFace {
  const date = new Date(CLOCK_SAMPLE_ISO);
  const hour = date.getUTCHours();
  const minute = date.getUTCMinutes();
  return {
    time: formatClockTime(hour, minute, block.props.format),
    seconds: String(date.getUTCSeconds()).padStart(2, '0'),
    meridiem: block.props.format === '12h' ? meridiem(hour) : '',
    date: formatClockDate(date),
    words: toWordClock(hour, minute),
    hands: { hour: hourHandDegrees(hour, minute), minute: minuteHandDegrees(minute) }
  };
}
